/**
 * Studio d'une animation (lot 5) : modifier le brouillon PAR FORMULAIRES,
 * sans JSON. À gauche, l'aperçu de la scène à l'instant choisi (curseur de
 * temps, repères des phrases et des étapes) ; à droite, l'inspecteur :
 *   - Scènes : ajouter, dupliquer, monter / descendre, supprimer ; titre,
 *     étape HORA, narration (sous-titres et texte dit) ; ses étapes (ajouter,
 *     ordre, action du catalogue, cible, ancrage sur une phrase…) ;
 *   - Objets : ajouter (montré dans la scène en cours), dupliquer, supprimer ;
 *     tous leurs champs (position, taille, couleur, cadre, formule…).
 * Les formulaires sont lus dans le schéma (`objectFields`, `stepFields`) ;
 * chaque modification est vérifiée (même validation que le serveur) et
 * enregistrée toute seule. Annuler / rétablir (Ctrl+Z, Ctrl+Maj+Z).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Copy, Plus, Redo2, Trash2, Undo2 } from 'lucide-react';
import {
  ACTION_LABELS,
  addObject,
  addScene,
  addStep,
  changeStepAction,
  duplicateObject,
  duplicateScene,
  getAt,
  isQualityWarning,
  JRAF_ACTIONS,
  JRAF_OBJECT_TYPES,
  JRAF_PALETTE,
  moveScene,
  moveStep,
  OBJECT_TYPE_LABELS,
  objectFields,
  objectUsage,
  removeObject,
  removeScene,
  removeStep,
  setAt,
  splitSentences,
  stepFields,
  estimateSpeechMs,
  type DraftPath,
  type FieldSpec,
  type JrafAction,
  type JrafIssue,
} from '@jeraisonne/animation';
import { Button, cn } from '@jeraisonne/ui';
import { TOOLS } from '@/features/toolbox/catalog';
import { AnimationIssues } from '@/features/animations/animation-test-bench';
import type { AnimationRow } from '@/lib/animations/api';
import { FieldEditor, type FieldContext } from './field-editor';
import { seconds1, StudioPreview } from './studio-preview';
import { useStudioDraft, type SaveState } from './use-studio-draft';

type Json = Record<string, unknown>;
type Tab = 'scenes' | 'objects';

/** au-delà de toute scène : l'aperçu montre son état final */
const END = 1e9;

const HORA_BG: Record<string, string> = {
  H: JRAF_PALETTE['hora-h'],
  O: JRAF_PALETTE['hora-o'],
  R: JRAF_PALETTE['hora-r'],
  A: JRAF_PALETTE['hora-a'],
};

const SAVE_LABEL: Record<SaveState, string> = {
  saved: 'Enregistré',
  pending: 'Modifications…',
  saving: 'Enregistrement…',
  error: 'Échec de l’enregistrement',
};

const SCENE_FIELDS: FieldSpec[] = [
  { key: 'title', kind: 'string', label: 'Titre', optional: false },
  { key: 'hora', kind: 'enum', label: 'Étape HORA', optional: true, options: ['H', 'O', 'R', 'A'] },
  {
    key: 'interactive',
    kind: 'boolean',
    label: 'Scène « À toi » (payante)',
    optional: true,
    default: false,
  },
  {
    key: 'transition',
    kind: 'enum',
    label: 'Transition',
    optional: true,
    options: ['fade', 'cut'],
    default: 'fade',
  },
];
const NARRATION_FIELDS: FieldSpec[] = [
  { key: 'text', kind: 'display', label: 'Sous-titres (ce qu’on lit)', optional: false },
  { key: 'say', kind: 'display', label: 'Ce que dit la voix (sans LaTeX)', optional: true },
];

const ADDABLE_TYPES = JRAF_OBJECT_TYPES.filter((t) => t !== 'image') as Exclude<
  (typeof JRAF_OBJECT_TYPES)[number],
  'image'
>[];

function scenesOf(draft: unknown): Json[] {
  const s = getAt(draft, ['scenes']);
  return Array.isArray(s) ? (s as Json[]) : [];
}
function objectsOf(draft: unknown): Json[] {
  const o = getAt(draft, ['objects']);
  return Array.isArray(o) ? (o as Json[]) : [];
}
function targetsText(t: unknown): string {
  return Array.isArray(t) ? t.join(', ') : typeof t === 'string' ? t : '';
}

export function AnimationStudio({
  row,
  toolIds,
}: {
  row: Pick<AnimationRow, 'id' | 'draft'>;
  toolIds?: readonly string[];
}) {
  const studio = useStudioDraft(row, toolIds);
  const { draft, edit } = studio;
  const scenes = scenesOf(draft);
  const objects = objectsOf(draft);
  const [sceneId, setSceneId] = useState<string>(() => String(scenes[0]?.id ?? ''));
  const [stepId, setStepId] = useState<string | null>(null);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('scenes');
  /** instant de l'aperçu ; au départ et à chaque scène : sa fin (tout est là) */
  const [t, setT] = useState(END);
  const sceneIndex = Math.max(
    0,
    scenes.findIndex((s) => s.id === sceneId),
  );
  const scene = scenes[sceneIndex];
  useEffect(() => {
    if (!scenes.some((s) => s.id === sceneId) && scenes[0]) setSceneId(String(scenes[0].id));
  }, [scenes, sceneId]);

  // aperçu : la scène affichée suit l'index dans le DERNIER document valide
  const good = studio.lastGood;
  const previewIndex = good
    ? Math.max(
        0,
        good.scenes.findIndex((s) => s.id === sceneId),
      )
    : 0;

  const onSet = useCallback(
    (path: DraftPath, value: unknown, coalesce = false) =>
      edit((d) => setAt(d, path, value), coalesce ? path.join('.') : undefined),
    [edit],
  );

  // raccourcis : annuler, rétablir
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const el = e.target as HTMLElement | null;
      // dans un champ, le navigateur annule la frappe
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      e.preventDefault();
      if (e.shiftKey) studio.redo();
      else studio.undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [studio]);

  // enregistrer avant de quitter la page (`flush` est stable)
  const { flush } = studio;
  useEffect(() => () => flush(), [flush]);

  const issues = studio.result;
  const errors: JrafIssue[] = issues && !issues.ok ? issues.errors : [];
  const warnings: JrafIssue[] = issues?.warnings ?? [];
  const quality = warnings.filter((w) => isQualityWarning(w));

  return (
    <div className="space-y-3">
      {studio.external && (
        <div
          role="alert"
          className="border-warning-500/40 bg-warning-50 flex flex-wrap items-center gap-2 rounded-xl border p-3 text-sm"
        >
          <span className="text-ink-800 font-semibold">
            Le brouillon a changé ailleurs (correction par l’IA, autre onglet).
          </span>
          <Button size="sm" variant="outline" onClick={studio.reload}>
            Recharger
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={studio.undo}
          disabled={!studio.canUndo}
          aria-label="Annuler"
        >
          <Undo2 className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={studio.redo}
          disabled={!studio.canRedo}
          aria-label="Rétablir"
        >
          <Redo2 className="h-4 w-4" aria-hidden />
        </Button>
        <span
          role="status"
          className={cn(
            'text-[13px] font-semibold',
            studio.save === 'error'
              ? 'text-error-600'
              : studio.save === 'saved'
                ? 'text-success-600'
                : 'text-ink-500',
          )}
        >
          {SAVE_LABEL[studio.save]}
          {studio.saveError ? ` : ${studio.saveError}` : ''}
        </span>
        <span className="text-ink-500 ml-auto text-[13px]">
          {errors.length > 0 ? (
            <span className="text-error-600 font-semibold">
              {errors.length} erreur{errors.length > 1 ? 's' : ''}
            </span>
          ) : (
            'Valide'
          )}
          {quality.length > 0 && ` · ${quality.length} défaut${quality.length > 1 ? 's' : ''}`}
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <div className="lg:sticky lg:top-4 lg:self-start">
          {good ? (
            <StudioPreview
              doc={good}
              sceneIndex={previewIndex}
              t={t}
              onTime={setT}
              selectedObject={tab === 'objects' ? objectId : null}
              focusStep={tab === 'scenes' ? stepId : null}
              onSelectStep={(id) => {
                setTab('scenes');
                setStepId(id);
              }}
            />
          ) : (
            <p className="text-ink-500 text-sm">
              Aperçu indisponible : le brouillon a des erreurs.
            </p>
          )}
        </div>

        <div className="min-w-0 space-y-3">
          {/* scènes : pastilles */}
          <div
            role="tablist"
            aria-label="Scènes"
            className="flex gap-1.5 overflow-x-auto pb-1 sm:flex-wrap"
          >
            {scenes.map((s, i) => {
              const hora = typeof s.hora === 'string' ? s.hora : null;
              const active = s.id === sceneId;
              return (
                <button
                  key={String(s.id)}
                  role="tab"
                  aria-selected={active}
                  type="button"
                  onClick={() => {
                    setSceneId(String(s.id));
                    setStepId(null);
                    setT(END);
                  }}
                  className={cn(
                    'inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[13px] font-semibold',
                    active
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-ink-200 text-ink-700 hover:bg-ink-50',
                  )}
                >
                  {hora && (
                    <span
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] text-white"
                      style={{ backgroundColor: HORA_BG[hora] }}
                      aria-hidden
                    >
                      {hora}
                    </span>
                  )}
                  {i + 1}. {String(s.title ?? s.id).slice(0, 28)}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                const r = addScene(draft, sceneIndex);
                edit(() => r.doc);
                setSceneId(r.id);
              }}
              className="border-ink-200 text-primary-600 hover:bg-ink-50 inline-flex min-h-10 shrink-0 items-center gap-1 rounded-full border border-dashed px-3 text-[13px] font-semibold"
            >
              <Plus className="h-4 w-4" aria-hidden /> Scène
            </button>
          </div>

          <div
            role="tablist"
            aria-label="Inspecteur"
            className="border-ink-200 flex gap-4 border-b"
          >
            {(['scenes', 'objects'] as const).map((k) => (
              <button
                key={k}
                role="tab"
                type="button"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                className={cn(
                  '-mb-px min-h-10 border-b-2 px-1 text-sm font-semibold',
                  tab === k
                    ? 'border-primary-500 text-primary-700'
                    : 'text-ink-500 border-transparent',
                )}
              >
                {k === 'scenes' ? 'Scène et étapes' : `Objets (${objects.length})`}
              </button>
            ))}
          </div>

          {tab === 'scenes' && scene && (
            <ScenePanel
              key={String(scene.id)}
              draft={draft}
              scene={scene}
              index={sceneIndex}
              count={scenes.length}
              objects={objects}
              toolIds={toolIds}
              stepId={stepId}
              onSelectStep={setStepId}
              onSet={onSet}
              edit={edit}
              onSceneId={(id) => setSceneId(id)}
              onShowObject={(id) => {
                setObjectId(id);
                setTab('objects');
              }}
            />
          )}
          {tab === 'objects' && (
            <ObjectPanel
              draft={draft}
              objects={objects}
              scene={scene}
              objectId={objectId}
              onSelect={setObjectId}
              onSet={onSet}
              edit={edit}
            />
          )}

          <details className="border-ink-200 rounded-xl border p-3">
            <summary className="text-ink-800 cursor-pointer text-sm font-semibold">
              Vérification ({errors.length} erreur{errors.length > 1 ? 's' : ''}, {quality.length}{' '}
              défaut
              {quality.length > 1 ? 's' : ''})
            </summary>
            <div className="mt-2">
              <AnimationIssues errors={errors} warnings={warnings} />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ scène

function ScenePanel({
  draft,
  scene,
  index,
  count,
  objects,
  toolIds,
  stepId,
  onSelectStep,
  onSet,
  edit,
  onSceneId,
  onShowObject,
}: {
  draft: unknown;
  scene: Json;
  index: number;
  count: number;
  objects: Json[];
  toolIds?: readonly string[];
  stepId: string | null;
  onSelectStep: (id: string | null) => void;
  onSet: (path: DraftPath, value: unknown, coalesce?: boolean) => void;
  edit: (next: (d: unknown) => unknown, key?: string) => void;
  onSceneId: (id: string) => void;
  onShowObject: (id: string) => void;
}) {
  const sid = String(scene.id);
  const base: DraftPath = ['scenes', index];
  const narration = (scene.narration ?? {}) as Json;
  const text = typeof narration.text === 'string' ? narration.text : '';
  const say = typeof narration.say === 'string' ? narration.say : undefined;
  const sentences = splitSentences(text);
  const saySentences = say ? splitSentences(say) : null;
  const steps = Array.isArray(scene.steps) ? (scene.steps as Json[]) : [];
  const [newAction, setNewAction] = useState<JrafAction>('object.show');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const objectList = objects.map((o) => ({ id: String(o.id), type: String(o.type) }));
  const tools = useMemo(
    () =>
      TOOLS.filter((t) => !toolIds || toolIds.includes(t.id)).map((t) => ({
        id: t.id,
        title: t.title,
      })),
    [toolIds],
  );
  const images = useMemo(() => {
    const a = getAt(draft, ['assets']);
    return Array.isArray(a)
      ? (a as Json[]).filter((x) => x.kind === 'image').map((x) => String(x.id))
      : [];
  }, [draft]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={index === 0}
          onClick={() => edit((d) => moveScene(d, sid, -1))}
        >
          <ArrowUp className="h-4 w-4" aria-hidden /> Avant
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={index === count - 1}
          onClick={() => edit((d) => moveScene(d, sid, 1))}
        >
          <ArrowDown className="h-4 w-4" aria-hidden /> Après
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            const r = duplicateScene(draft, sid);
            edit(() => r.doc);
            onSceneId(r.id);
          }}
        >
          <Copy className="h-4 w-4" aria-hidden /> Dupliquer
        </Button>
        {!confirmDelete ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={count <= 1}
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="h-4 w-4" aria-hidden /> Supprimer
          </Button>
        ) : (
          <span className="flex items-center gap-2 text-[13px]">
            Supprimer cette scène ?
            <Button
              size="sm"
              variant="destructive"
              onClick={() => edit((d) => removeScene(d, sid))}
            >
              Supprimer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
              Non
            </Button>
          </span>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {SCENE_FIELDS.map((f) => (
          <div key={f.key} className={f.key === 'title' ? 'sm:col-span-2' : ''}>
            <FieldEditor
              spec={f}
              value={scene[f.key]}
              path={[...base, f.key]}
              onSet={onSet}
              ctx={emptyCtx}
            />
          </div>
        ))}
      </div>

      <div className="space-y-2">
        {NARRATION_FIELDS.map((f) => (
          <FieldEditor
            key={f.key}
            spec={f}
            value={narration[f.key]}
            path={[...base, 'narration', f.key]}
            onSet={onSet}
            ctx={emptyCtx}
          />
        ))}
        <p className="text-ink-500 text-[12px]">
          {sentences.length} phrase{sentences.length > 1 ? 's' : ''} · environ{' '}
          {seconds1(estimateSpeechMs(say ?? text))} à voix haute
          {saySentences && saySentences.length !== sentences.length && (
            <span className="text-error-600 font-semibold">
              {' '}
              · la voix a {saySentences.length} phrase{saySentences.length > 1 ? 's' : ''} : il en
              faut {sentences.length} (une phrase = un sous-titre)
            </span>
          )}
          {typeof narration.audioAssetId === 'string' &&
            ' · ta voix enregistrée (à refaire si tu changes le texte)'}
        </p>
      </div>

      <section aria-label="Étapes" className="space-y-2">
        <h3 className="text-ink-900 text-sm font-bold">Étapes ({steps.length})</h3>
        <ol className="divide-ink-100 border-ink-200 divide-y rounded-xl border bg-white">
          {steps.map((st, i) => {
            const id = String(st.id);
            const action = st.action as JrafAction;
            const open = stepId === id;
            const timing = [
              typeof st.atSentence === 'number' ? `phrase ${st.atSentence + 1}` : null,
              typeof st.with === 'string' ? `avec ${st.with}` : null,
              typeof st.delayMs === 'number' && st.delayMs > 0 ? `+${st.delayMs} ms` : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <li key={id} data-step={id}>
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => onSelectStep(open ? null : id)}
                    className="hover:bg-ink-50 min-h-10 min-w-0 flex-1 rounded-lg px-2 text-left text-sm"
                  >
                    <span className="text-ink-400 mr-1 tabular-nums">{i + 1}.</span>
                    <span className="text-ink-900 font-semibold">
                      {ACTION_LABELS[action] ?? action}
                    </span>
                    {targetsText(st.target) && (
                      <span className="text-ink-600"> · {targetsText(st.target)}</span>
                    )}
                    {timing && <span className="text-ink-400"> · {timing}</span>}
                  </button>
                  <IconButton
                    label={`Monter l’étape ${id}`}
                    disabled={i === 0}
                    onClick={() => edit((d) => moveStep(d, sid, id, -1))}
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden />
                  </IconButton>
                  <IconButton
                    label={`Descendre l’étape ${id}`}
                    disabled={i === steps.length - 1}
                    onClick={() => edit((d) => moveStep(d, sid, id, 1))}
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden />
                  </IconButton>
                  <IconButton
                    label={`Supprimer l’étape ${id}`}
                    onClick={() => edit((d) => removeStep(d, sid, id))}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </IconButton>
                </div>
                {open && (
                  <div className="bg-ink-50/60 space-y-2 px-3 pb-3 pt-1">
                    <div>
                      <label
                        className="text-ink-700 mb-0.5 block text-xs font-semibold"
                        htmlFor={`action-${id}`}
                      >
                        Action
                      </label>
                      <select
                        id={`action-${id}`}
                        className="border-ink-200 min-h-10 w-full rounded-lg border bg-white px-2.5 text-sm"
                        value={action}
                        onChange={(e) =>
                          edit((d) => changeStepAction(d, sid, id, e.target.value as JrafAction))
                        }
                      >
                        {JRAF_ACTIONS.map((a) => (
                          <option key={a} value={a}>
                            {ACTION_LABELS[a]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {safeStepFields(action).map((f) => (
                        <div
                          key={f.key}
                          className={
                            [
                              'display',
                              'tex',
                              'object',
                              'list',
                              'target',
                              'variant',
                              'point',
                            ].includes(f.kind)
                              ? 'sm:col-span-2'
                              : ''
                          }
                        >
                          <FieldEditor
                            spec={f}
                            value={st[f.key]}
                            path={[...base, 'steps', i, f.key]}
                            onSet={onSet}
                            ctx={{
                              objects: objectList,
                              stepsBefore: steps.slice(0, i).map((x) => String(x.id)),
                              sentences,
                              tools,
                              images,
                              choices: Array.isArray(st.choices) ? (st.choices as string[]) : [],
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {typeof st.target === 'string' && st.target && (
                      <button
                        type="button"
                        onClick={() => onShowObject(String(st.target))}
                        className="text-primary-600 min-h-9 text-[13px] font-semibold"
                      >
                        Voir l’objet « {String(st.target)} »
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Action de la nouvelle étape"
            className="border-ink-200 min-h-10 rounded-lg border bg-white px-2.5 text-sm"
            value={newAction}
            onChange={(e) => setNewAction(e.target.value as JrafAction)}
          >
            {JRAF_ACTIONS.map((a) => (
              <option key={a} value={a}>
                {ACTION_LABELS[a]}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const at = stepId ? steps.findIndex((s) => s.id === stepId) : -1;
              const r = addStep(draft, sid, newAction, at >= 0 ? { afterIndex: at } : {});
              edit(() => r.doc);
              onSelectStep(r.id);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden /> Ajouter l’étape
          </Button>
        </div>
      </section>
    </div>
  );
}

function safeStepFields(action: JrafAction): FieldSpec[] {
  try {
    return stepFields(action);
  } catch {
    return [];
  }
}

const emptyCtx: FieldContext = {
  objects: [],
  stepsBefore: [],
  sentences: [],
  tools: [],
  images: [],
  choices: [],
};

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="text-ink-500 hover:bg-ink-100 hover:text-ink-900 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg disabled:opacity-30"
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ objets

function ObjectPanel({
  draft,
  objects,
  scene,
  objectId,
  onSelect,
  onSet,
  edit,
}: {
  draft: unknown;
  objects: Json[];
  scene: Json | undefined;
  objectId: string | null;
  onSelect: (id: string | null) => void;
  onSet: (path: DraftPath, value: unknown, coalesce?: boolean) => void;
  edit: (next: (d: unknown) => unknown, key?: string) => void;
}) {
  const usage = useMemo(() => objectUsage(draft), [draft]);
  const [onlyScene, setOnlyScene] = useState(true);
  const [newType, setNewType] = useState<(typeof ADDABLE_TYPES)[number]>('text');
  const [confirm, setConfirm] = useState<string | null>(null);
  const sid = scene ? String(scene.id) : null;
  const visible = objects.filter(
    (o) => !onlyScene || !sid || (usage.get(String(o.id)) ?? []).includes(sid) || o.id === objectId,
  );
  const objectList = objects.map((o) => ({ id: String(o.id), type: String(o.type) }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-ink-700 flex min-h-10 items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-primary-500 h-4 w-4"
            checked={onlyScene}
            onChange={(e) => setOnlyScene(e.target.checked)}
          />
          Seulement ceux de cette scène
        </label>
        <span className="ml-auto flex items-center gap-2">
          <select
            aria-label="Type du nouvel objet"
            className="border-ink-200 min-h-10 rounded-lg border bg-white px-2.5 text-sm"
            value={newType}
            onChange={(e) => setNewType(e.target.value as (typeof ADDABLE_TYPES)[number])}
          >
            {ADDABLE_TYPES.map((t) => (
              <option key={t} value={t}>
                {OBJECT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const r = addObject(draft, newType);
              // montré dans la scène en cours (un groupe n'a rien à montrer)
              const doc =
                sid && newType !== 'group'
                  ? addStep(r.doc, sid, 'object.show', { target: r.id }).doc
                  : r.doc;
              edit(() => doc);
              onSelect(r.id);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden /> Ajouter
          </Button>
        </span>
      </div>

      <ul className="divide-ink-100 border-ink-200 divide-y rounded-xl border bg-white">
        {visible.length === 0 && (
          <li className="text-ink-500 p-3 text-sm">Aucun objet dans cette scène.</li>
        )}
        {visible.map((o) => {
          const id = String(o.id);
          const index = objects.indexOf(o);
          const type = String(o.type) as (typeof JRAF_OBJECT_TYPES)[number];
          const open = objectId === id;
          const used = usage.get(id) ?? [];
          return (
            <li key={id} data-object-row={id}>
              <div className="flex items-center gap-1 px-2 py-1.5">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => onSelect(open ? null : id)}
                  className="hover:bg-ink-50 min-h-10 min-w-0 flex-1 rounded-lg px-2 text-left text-sm"
                >
                  <span className="text-ink-900 font-semibold">{id}</span>
                  <span className="text-ink-500"> · {OBJECT_TYPE_LABELS[type] ?? type}</span>
                  <span className="text-ink-400">
                    {' '}
                    ·{' '}
                    {used.length
                      ? `${used.length} scène${used.length > 1 ? 's' : ''}`
                      : 'aucune étape'}
                  </span>
                </button>
                <IconButton
                  label={`Dupliquer ${id}`}
                  onClick={() => {
                    const r = duplicateObject(draft, id);
                    edit(() => r.doc);
                    onSelect(r.id);
                  }}
                >
                  <Copy className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  label={`Supprimer ${id}`}
                  onClick={() => (used.length ? setConfirm(id) : edit((d) => removeObject(d, id)))}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </IconButton>
              </div>
              {confirm === id && (
                <p className="flex flex-wrap items-center gap-2 px-3 pb-2 text-[13px]">
                  Utilisé dans {used.length} scène{used.length > 1 ? 's' : ''} : ses étapes partent
                  avec lui.
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      edit((d) => removeObject(d, id));
                      setConfirm(null);
                    }}
                  >
                    Supprimer
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirm(null)}>
                    Non
                  </Button>
                </p>
              )}
              {open && index >= 0 && (
                <div className="bg-ink-50/60 grid gap-2 px-3 pb-3 pt-1 sm:grid-cols-2">
                  {safeObjectFields(type).map((f) => (
                    <div
                      key={f.key}
                      className={
                        ['display', 'tex', 'object', 'list', 'target', 'variant', 'point'].includes(
                          f.kind,
                        )
                          ? 'sm:col-span-2'
                          : ''
                      }
                    >
                      <FieldEditor
                        spec={f}
                        value={o[f.key]}
                        path={['objects', index, f.key]}
                        onSet={onSet}
                        ctx={{ ...emptyCtx, objects: objectList }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function safeObjectFields(type: (typeof JRAF_OBJECT_TYPES)[number]): FieldSpec[] {
  try {
    return objectFields(type);
  } catch {
    return [];
  }
}
